import React, { useState } from "react";
import api from "../../../../../utils/apiConfig";
import { useAuth } from "../../../../../contexts/AuthContext";
import Modal from "../../../../shared/Modal";

const MilitaryModal = ({ isOpen, onClose, member, onUpdate }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    branchOfService: "",
    rank: "",
    typeOfService: "Active Duty",
    startDate: "",
    endDate: "",
    isTimeFunded: false,
    hasRetirementPay: false,
    hasVaDisability: false,
    vaDisabilityPercentage: "",
    description: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(
        `/advisor-portal/client-database/household-profile/${member.householdId}/military`,
        {
          ...formData,
          vaDisabilityPercentage: formData.vaDisabilityPercentage
            ? parseInt(formData.vaDisabilityPercentage)
            : null,
          memberId: member.id,
        },
      );

      if (response.data) {
        onUpdate(response.data);
        onClose();
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to save military information",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="2xl"
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Add Military Service</h2>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Branch of Service*
              </label>
              <select
                name="branchOfService"
                value={formData.branchOfService}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select Branch</option>
                <option value="army">Army</option>
                <option value="navy">Navy</option>
                <option value="air_force">Air Force</option>
                <option value="marine_corps">Marine Corps</option>
                <option value="coast_guard">Coast Guard</option>
                <option value="space_force">Space Force</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rank*
              </label>
              <input
                type="text"
                name="rank"
                value={formData.rank}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type of Service*
              </label>
              <select
                name="typeOfService"
                value={formData.typeOfService}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="Active Duty">Active Duty</option>
                <option value="Reserve">Reserve</option>
                <option value="National Guard">National Guard</option>
                <option value="Veteran">Veteran</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date*
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                min={formData.startDate}
                className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isTimeFunded"
                checked={formData.isTimeFunded}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Time Funded
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="hasRetirementPay"
                checked={formData.hasRetirementPay}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Receives Retirement Pay
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="hasVaDisability"
                checked={formData.hasVaDisability}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Has VA Disability
              </label>
            </div>

            {formData.hasVaDisability && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  VA Disability Percentage
                </label>
                <input
                  type="number"
                  name="vaDisabilityPercentage"
                  value={formData.vaDisabilityPercentage}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  required
                  className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default MilitaryModal;
